import React from 'react';
import { BlockStack, InlineStack, Text, Banner, TextField, Spinner } from '@shopify/polaris';

const money = value => Number(value || 0).toFixed(2);
const MIN_REASON_LENGTH = 10;
const AMOUNT_PATTERN = /^\d+(\.\d{1,2})?$/;

// Same rules as the review step of full order editing. A blank or zero amount
// means no discount is being applied (clearing a discount is not offered here).
export function getDiscountInput(discount, maxDiscount) {
  const rawAmount = String(discount?.amount ?? '').trim();
  const reason = String(discount?.reason ?? '').trim();
  const amountInvalid = rawAmount !== '' && !AMOUNT_PATTERN.test(rawAmount);
  const amount = amountInvalid ? 0 : Number(rawAmount) || 0;
  let amountError = '';
  if (amountInvalid) amountError = 'Enter a valid discount amount';
  else if (rawAmount !== '' && amount === 0) amountError = 'Enter an amount above 0';
  else if (maxDiscount !== null && maxDiscount !== undefined && amount > Number(maxDiscount)) {
    amountError = `Up to ${money(maxDiscount)}`;
  }
  const reasonError = amount > 0 && reason.length < MIN_REASON_LENGTH
    ? `Enter a reason of at least ${MIN_REASON_LENGTH} characters.`
    : '';
  const active = amount > 0;
  return {
    active,
    amount,
    reason,
    amountError,
    reasonError,
    valid: !amountError && !reasonError
  };
}

export default function PaymentDiscountFields({
  order,
  discount,
  onChange,
  capabilities,
  preview,
  status,
  error,
  saveError
}) {
  if (!capabilities?.canDiscount) {
    return (
      <BlockStack gap="100">
        <Text as="h3" variant="headingSm">Special discount</Text>
        <Text as="p" tone="subdued">
          {capabilities?.reasons?.discount || capabilities?.reason || 'The discount cannot be changed on this order'}
        </Text>
      </BlockStack>
    );
  }

  const existing = order?.special_discount;
  const existingAmount = Number(existing?.amount) || 0;
  const maxDiscount = preview ? Number(preview.max_discount) || 0 : null;
  const input = getDiscountInput(discount, maxDiscount);
  const blocking = preview?.blocking_errors || [];

  return (
    <BlockStack gap="300">
      <Text as="h3" variant="headingSm">Special discount</Text>
      {existingAmount > 0 && (
        <Text as="p" tone="subdued">
          {`Current discount: ${money(existingAmount)}${existing.reason ? ` (${existing.reason})` : ''}. A new amount replaces it.`}
        </Text>
      )}
      <TextField
        label="Discount amount"
        value={discount.amount}
        onChange={value => onChange({ ...discount, amount: value })}
        autoComplete="off"
        inputMode="decimal"
        error={input.amountError || undefined}
        helpText={maxDiscount !== null ? `Up to ${money(maxDiscount)}` : 'Leave empty for no discount'}
      />
      <TextField
        label="Discount reason"
        value={discount.reason}
        onChange={value => onChange({ ...discount, reason: value })}
        autoComplete="off"
        error={input.reasonError || undefined}
        helpText={`At least ${MIN_REASON_LENGTH} characters`}
      />

      {saveError && (
        <Banner tone="critical" title="Discount was not applied">
          <p>{saveError}</p>
        </Banner>
      )}

      {input.active && input.valid && status === 'loading' && (
        <InlineStack gap="200" blockAlign="center">
          <Spinner size="small" accessibilityLabel="Loading" />
          <Text as="span" tone="subdued">Calculating the new total</Text>
        </InlineStack>
      )}

      {status === 'error' && (
        <Banner tone="critical" title="Could not calculate the discount">
          <p>{error}</p>
        </Banner>
      )}

      {blocking.length > 0 && (
        <Banner tone="critical" title="Fix these before applying">
          <BlockStack gap="100">
            {blocking.map((message, index) => <p key={`${index}-${message}`}>{message}</p>)}
          </BlockStack>
        </Banner>
      )}

      {preview && preview.warnings?.length > 0 && (
        <Banner tone="warning" title="Check before applying">
          <BlockStack gap="100">
            {preview.warnings.map((message, index) => <p key={`${index}-${message}`}>{message}</p>)}
          </BlockStack>
        </Banner>
      )}

      {preview && status === 'ready' && (
        <InlineStack gap="300" blockAlign="baseline">
          <Text as="span" tone="subdued">Previous total</Text>
          <Text as="span" tone="subdued">{money(preview.totals.before)}</Text>
          <Text as="span" tone="subdued">New total</Text>
          <Text as="span" variant="headingMd">{money(preview.totals.after)}</Text>
          {preview.payment && (
            <>
              <Text as="span" tone="subdued">Amount due</Text>
              <Text as="span">{money(preview.payment.amount_due)}</Text>
            </>
          )}
        </InlineStack>
      )}
    </BlockStack>
  );
}
